/**
 * PremiumPhoneInput - country dial selector (react-select + flag-icons) + tel input.
 *
 * Design: [ PL flag +48 | 512 345 678 ]
 *
 * The component stores/returns a full phone value with the dial code, but keeps
 * the text input focused on the local number for cleaner forms.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Select from 'react-select';
import { countries } from 'countries-list';

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeForDialMatch = (value) => String(value || '').replace(/[^\d+]/g, '');

const stripDialPrefix = (value, dial) => {
  const text = String(value || '').trim();
  if (!text || !dial) return text;
  return text.replace(new RegExp(`^\\s*${escapeRegExp(dial)}[\\s.-]*`), '');
};

const inferCountryFromValue = (value, options) => {
  const normalized = normalizeForDialMatch(value);
  if (!normalized.startsWith('+')) return null;

  return [...options]
    .sort((a, b) => b.dial.length - a.dial.length || a.label.localeCompare(b.label))
    .find((option) => normalized.startsWith(option.dial)) || null;
};

const stripKnownDialPrefix = (value, options, preferredDial) => {
  const text = String(value || '').trim();
  if (!text) return '';

  const preferredStripped = stripDialPrefix(text, preferredDial);
  if (preferredStripped !== text) return preferredStripped;

  const inferred = inferCountryFromValue(text, options);
  if (inferred) return stripDialPrefix(text, inferred.dial);

  return text.replace(/^\+\d+\s*/, '').trimStart();
};

const PremiumPhoneInput = ({
  value = '',
  onChange,
  country = 'PL',
  placeholder,
  disabled = false,
  hasError = false,
  hasSuccess = false,
  inputProps = {},
  className = '',
  locale = 'pl',
  controlHeight = 42,
  dialSelectWidth = '6.75rem',
  searchPlaceholder = 'Search...',
  selectAriaLabel = 'Country calling code',
}) => {
  const [focused, setFocused] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(String(country || 'PL').toUpperCase());

  const phoneDialOptions = useMemo(() => {
    let displayNames = null;
    try { displayNames = new Intl.DisplayNames([locale], { type: 'region' }); } catch {}
    return Object.entries(countries)
      .filter(([, info]) => info.phone && info.phone.length > 0)
      .map(([code, info]) => ({
        value: code,
        dial: '+' + info.phone[0],
        flagCode: code.toLowerCase(),
        label: displayNames ? (displayNames.of(code) || info.name) : info.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [locale]);

  useEffect(() => {
    const inferred = inferCountryFromValue(value, phoneDialOptions);
    const requestedCountry = String(country || 'PL').toUpperCase();
    const requestedExists = phoneDialOptions.some((option) => option.value === requestedCountry);
    const nextCountry = inferred?.value || (requestedExists ? requestedCountry : 'PL');

    setSelectedCountry((current) => (current === nextCountry ? current : nextCountry));
  }, [country, phoneDialOptions, value]);

  const selectedOption = useMemo(
    () => phoneDialOptions.find((option) => option.value === selectedCountry) || phoneDialOptions.find((option) => option.value === 'PL'),
    [selectedCountry, phoneDialOptions]
  );

  const selectedDial = selectedOption?.dial || '+48';
  const defaultPlaceholder = placeholder || '123 456 789';
  const localValue = useMemo(
    () => stripKnownDialPrefix(value, phoneDialOptions, selectedDial),
    [phoneDialOptions, selectedDial, value]
  );

  const handleCountryChange = useCallback((selected) => {
    if (!selected) return;
    const newDial = selected.dial;
    setSelectedCountry(selected.value);
    if (onChange) {
      const stripped = stripKnownDialPrefix(value, phoneDialOptions, selectedDial);
      onChange(stripped ? `${newDial} ${stripped}` : newDial);
    }
  }, [onChange, phoneDialOptions, selectedDial, value]);

  const handlePhoneChange = useCallback((e) => {
    if (!onChange) return;

    const nextValue = e.target.value;
    const trimmed = nextValue.trim();

    if (!trimmed) {
      onChange('');
      return;
    }

    if (trimmed.startsWith('+')) {
      const inferred = inferCountryFromValue(trimmed, phoneDialOptions);
      if (inferred) setSelectedCountry(inferred.value);
      onChange(trimmed);
      return;
    }

    onChange(`${selectedDial} ${trimmed}`);
  }, [onChange, phoneDialOptions, selectedDial]);

  let borderColor = '#d1d5db';
  if (hasError) borderColor = '#f87171';
  else if (focused) borderColor = '#60a5fa';
  else if (hasSuccess) borderColor = '#6ee7b7';

  const bgColor = hasSuccess ? 'rgba(236,253,245,0.3)' : disabled ? '#f8fafc' : '#ffffff';
  const {
    onFocus,
    onBlur,
    className: inputClassName = '',
    style: inputStyle = {},
    ...restInputProps
  } = inputProps || {};

  return (
    <div className={`flex min-w-0 max-w-full items-stretch ${className}`.trim()}>
      <Select
        options={phoneDialOptions}
        value={selectedOption}
        onChange={handleCountryChange}
        isSearchable
        isDisabled={disabled}
        aria-label={selectAriaLabel}
        placeholder={searchPlaceholder}
        noOptionsMessage={() => searchPlaceholder}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        formatOptionLabel={({ flagCode, dial, label }, { context }) =>
          context === 'menu' ? (
            <div className="flex items-center gap-2">
              <span className={'fi fi-' + flagCode + ' rounded-sm'} style={{ width: '1.15rem', display: 'inline-block' }} />
              <span className="text-slate-700">{label}</span>
              <span className="text-slate-400 ml-auto text-xs">{dial}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className={'fi fi-' + flagCode + ' rounded-sm'} style={{ width: '1.15rem', display: 'inline-block' }} />
              <span className="text-xs text-slate-600 font-medium">{dial}</span>
            </div>
          )
        }
        styles={{
          container: (base) => ({ ...base, width: dialSelectWidth, minWidth: dialSelectWidth, maxWidth: dialSelectWidth, flexShrink: 0 }),
          control: (base, state) => ({
            ...base,
            borderRadius: '8px 0 0 8px',
            borderRight: 'none',
            borderColor: state.isFocused ? '#60a5fa' : borderColor,
            backgroundColor: bgColor,
            boxShadow: state.isFocused ? '0 0 0 2px rgba(96,165,250,0.4)' : 'none',
            minHeight: controlHeight,
            maxWidth: '100%',
            cursor: 'pointer',
            '&:hover': { borderColor: state.isFocused ? '#60a5fa' : '#cbd5e1' },
          }),
          valueContainer: (base) => ({ ...base, padding: '2px 6px' }),
          indicatorSeparator: (base) => ({ ...base, backgroundColor: '#e2e8f0', marginTop: 8, marginBottom: 8 }),
          dropdownIndicator: (base) => ({ ...base, padding: '0 4px', color: '#94a3b8' }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({
            ...base,
            width: 'min(300px, calc(100vw - 2rem))',
            maxWidth: 'calc(100vw - 2rem)',
            borderRadius: 10,
            boxShadow: '0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)',
          }),
          option: (base, state) => ({
            ...base,
            fontSize: '0.875rem',
            padding: '8px 12px',
            backgroundColor: state.isSelected ? '#113F67' : state.isFocused ? '#f1f5f9' : 'white',
            color: state.isSelected ? 'white' : '#334155',
            cursor: 'pointer',
          }),
        }}
      />
      <input
        {...restInputProps}
        type="tel"
        value={localValue}
        onChange={handlePhoneChange}
        onFocus={(e) => { setFocused(true); onFocus?.(e); }}
        onBlur={(e) => { setFocused(false); onBlur?.(e); }}
        disabled={disabled}
        placeholder={defaultPlaceholder}
        className={'flex-1 min-w-0 rounded-r-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ' + (
          hasError
            ? 'border-red-400 bg-white'
            : hasSuccess
              ? 'border-emerald-300 bg-emerald-50/30'
              : 'border-slate-300 bg-white'
        ) + (inputClassName ? ` ${inputClassName}` : '')}
        style={{
          borderLeft: 'none',
          minHeight: controlHeight,
          fontSize: '0.9375rem',
          opacity: disabled ? 0.6 : 1,
          ...inputStyle,
        }}
        autoComplete={inputProps?.autoComplete || 'tel'}
      />
    </div>
  );
};

export default PremiumPhoneInput;
